import * as React from 'react';
import { CSSProperties } from 'react';
import * as classNames from 'classnames';
import { Popover, Position as BlueprintPosition } from '@blueprintjs/core';

import './styles.scss';
import Icon, { IconsAvailable } from '../Icon';
import styled from 'styled-components';

const DynamicElement = styled(({ as: Element = 'button', ...props }) => <Element {...props} />)`
    position: relative;
    &::after {
        width: 300px;
        content: ${({ isTooltip }) => (isTooltip ? 'attr(data-title)' : '')};
        position: absolute;
        bottom: 142%;
        background-color: #ededed;
        color: #000;
        padding: 5px;
        border-radius: 4px;
        opacity: 0;
        pointer-events: none;
        transition: opacity 0.2s ease-in-out;
        font-size: 12px;
        z-index: 10;
        box-shadow: rgba(0, 0, 0, 0.3) 0 2px 10px;
        font-weight: 400;
        white-space: normal;
    }

    &:hover::after {
        opacity: 1;
    }
`;

export interface IButtonIconProps {
    /** style to pass */
    style?: CSSProperties;
    /** size to use */
    size?: 'large' | 'default' | 'small' | 'x-small';
    /** click event */
    onClick?: (event) => void;
    /** intent variation */
    intent?: 'default' | 'primary' | 'success' | 'danger' | 'link' | 'neutral';
    /** disable clicking */
    disabled?: boolean;
    /** tooltip */
    tooltip?: string;
    /** disable clicking and display loader */
    loading?: boolean;
    /** type of element to use */
    type?: 'button' | 'submit' | 'a';
    /** Disable shadow */
    disableShadow?: boolean;
    /** Icon to use within button */
    icon: IconsAvailable;
    /** className to append */
    className?: string;
    /** DOM element name attribute */
    name?: string;
    /** DOM element id attribute */
    id?: string;
    /** Label to display */
    label?: string;
    /** Dropdown menu content */
    menu?: React.ReactElement;
    /** style to use */
    layout?: 'default' | 'icon-only';
}

export type AnimationOptions = 'bounce';

interface IState {
    isOpen: boolean;
}

/**
 * Displays button
 */
export default class ButtonIcon extends React.Component<IButtonIconProps, IState> {
    static displayName: string = 'ButtonIcon';

    constructor (props: IButtonIconProps) {
        super(props);
        this.state = {
            isOpen: false,
        };
    }

    handleClosePopover = (e?: React.SyntheticEvent<HTMLElement>) => {
        this.setState({
            isOpen: false,
        });
    };

    handleToggle = (e) => {
        e.preventDefault();
        this.setState({
            isOpen: true,
        });
    };

    RenderButton = () => {
        return (
            <div className="button-icon-inner">
                <div className="button-icon-main">
                    <Icon
                        width={12}
                        height={12}
                        kind={this.props.icon}
                        wrapperClassName="icon"
                    />
                    {this.props.loading && (
                        <div className="loader">
                            <Icon width={12} height={12} kind="loading"/>
                        </div>
                    )}
                </div>
                {!!this.props.label && (
                    <div className="label">{this.props.label}</div>
                )}
            </div>
        );
    };

    render () {
        return (
            <DynamicElement
                as={this.props.type === 'a' ? 'a' : 'button'}
                type={this.props.type && this.props.type === 'submit' ? 'submit' : undefined}
                className={classNames('button-icon', this.props.className, {
                    'disabled': this.props.disabled,
                    'loading': this.props.loading,
                    'noshadow': this.props.disableShadow,

                    'size-extra-small': this.props.size === 'x-small',
                    'size-small': this.props.size === 'small',
                    'size-large': this.props.size === 'large',

                    'intent-default': this.props.intent === 'default' || !this.props.intent,
                    'intent-primary': this.props.intent === 'primary',
                    'intent-success': this.props.intent === 'success',
                    'intent-danger': this.props.intent === 'danger',
                    'intent-link': this.props.intent === 'link',
                    'intent-neutral': this.props.intent === 'neutral',

                    'layout-default': this.props.layout === 'default' || !this.props.layout,
                    'layout-icon-only': this.props.layout === 'icon-only',
                })}
                style={this.props.style}
                onClick={(e) => {
                    if (this.props.menu) {
                        this.handleToggle(e);
                    }
                    if (this.props.onClick) {
                        this.props.onClick(e);
                    }
                }}
                name={this.props.name}
                id={this.props.id}
                isTooltip={!!this.props.tooltip}
                data-title={this.props.tooltip}
            >
                {this.props.menu ? (
                    <Popover
                        content={this.props.menu}
                        target={(
                            <this.RenderButton/>
                        )}
                        hasBackdrop={false}
                        isOpen={this.state.isOpen}
                        onClose={this.handleClosePopover}
                        portalClassName={'popover-default-portal'}
                        popoverClassName={'popover-default-popover nopadding'}
                        position={BlueprintPosition.BOTTOM}
                        minimal={true}
                        usePortal={true}
                        modifiers={{
                            offset: {
                                offset: '0, 10',
                            },
                        }}
                    />
                ) : (
                    <this.RenderButton/>
                )}
            </DynamicElement>
        );
    }
}
